import { useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Pagination,
  PaginationItem,
  Typography,
} from "@mui/material";
import useDashboardList from "../../hooks/useDashboardList";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import Background from "../../components/Background";
import prevIcon from "../../assets/images/prevIcon.svg";
import nextIcon from "../../assets/images/nextIcon.svg";

export const PreviousSlot = ({ onPreviousClick }) => (
  <Box sx={{ display: "flex", alignItems: "center" }} onClick={onPreviousClick}>
    <img src={prevIcon} alt="Prev Icon" />
    <Box sx={{ marginLeft: "5px" }}>Previous</Box>
  </Box>
);

export const NextSlot = ({ page, totalPages, onNextClick }) => (
  <Box
    onClick={onNextClick}
    sx={{ display: "flex", alignItems: "center" }}
    style={{ pointerEvents: page === totalPages ? "none" : "auto" }}
  >
    <Box sx={{ marginRight: "5px" }}>Next</Box>
    <img src={nextIcon} alt="Next Icon" />
  </Box>
);

export default function IndustryHub({ searchTerm }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [page, setPage] = useState(1);

  const paginationSlots = {
    previous: PreviousSlot,
    next: NextSlot,
  };

  const request = useMemo(
    () => ({
      url: "/v1/web/learn/logo-list",
      method: "GET",
      params: { page, limit: 12, searchKey: searchTerm ?? "" },
    }),
    [page, searchTerm],
  );
  const { data, loading } = useDashboardList(request, token);
  const sponsorsList = data.results;
  const totalRecords = data.total;

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePreviousClick = () => {
    if (page > 1) {
      setPage((page) => page - 1);
    }
  };

  const handleNextClick = () => {
    setPage((page) => page + 1);
  };

  return (
    <>
      <Background dashboardChrome={true}>
        {loading && <Loader />}
        <Box className="dashboard-main-div">
          <Box
            sx={{
              padding: {
                xs: "10px 0 20px",
                sm: "10px 0 20px",
                md: "10px 0 20px",
                lg: "10px 0 20px",
                xl: "20px 0 30px",
              },
            }}
          >
            <Container maxWidth="xl">
              <Box
                sx={{
                  padding: {
                    xs: 0,
                    sm: "0 10px",
                    md: "0 10px",
                    lg: "0 30px",
                  },
                  // No top margin: unlike Learn there is no tabs row to
                  // host, so content starts right under the header.
                  margin: {
                    xs: 0,
                    sm: "0 10px 10px",
                    md: "0 10px 10px",
                    lg: "0 30px 30px",
                  },
                }}
              >
                <Grid container spacing={{ lg: 3, sm: 2, xs: 1 }}>
                  <Grid
                    xl={12}
                    sx={{
                      bgcolor: "#fff",
                      padding: "20px",
                      borderRadius: "12px",
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: "left",
                        fontSize: { xs: "16px", sm: "18px" },
                        fontWeight: 500,
                        color: "#2A4876",
                      }}
                    >
                      Explore the organizations shaping aviation on Echo! Click
                      any organization to open its hub and see all content,
                      including videos, presentations, articles, and webinars,
                      all in one place. Gain exclusive industry insights, expand
                      your skill set, and stay informed.
                    </Typography>
                  </Grid>
                  {sponsorsList.map((sponsor) => (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      md={4}
                      lg={3}
                      xl={3}
                      key={sponsor.createdBy}
                      onClick={() =>
                        navigate(
                          `/dashboard?active=learn&corporateId=${sponsor.createdBy}`,
                        )
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: "50%",
                            backgroundColor: "#fff",
                            border: "1px solid #2A487630",
                            width: "100%",
                            maxWidth: "230px",
                            aspectRatio: "1 / 1",
                            overflow: "hidden",
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={sponsor.logo}
                            alt={sponsor.companyName}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            textAlign: "center",
                            fontSize: { xs: "14px", sm: "16px" },
                            fontWeight: 600,
                            marginTop: "8px",
                            color: "#2A4876",
                            maxWidth: "90%", // prevents long names from spilling out
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {sponsor?.companyName}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              {totalRecords > 12 && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: {
                      xs: "15px 0",
                      sm: "30px",
                      md: "30px",
                      lg: "0 40px 0",
                    },
                    "& ul li:last-child": {
                      position: {
                        xs: "unset",
                        md: "absolute",
                      },
                      right: "6%",
                    },
                    "& ul li:first-child": {
                      position: {
                        xs: "unset",
                        md: "absolute",
                      },
                      left: "6%",
                    },
                  }}
                >
                  <Pagination
                    count={Math.ceil(totalRecords / 12)}
                    page={page}
                    onChange={handlePageChange}
                    renderItem={(item) => (
                      <PaginationItem
                        sx={{
                          fontSize: {
                            xs: "12px",
                            sm: "14px",
                            md: "16px",
                          },
                          minWidth: {
                            xs: "28px",
                            sm: "32px",
                          },
                          height: {
                            xs: "28px",
                            sm: "32px",
                          },
                          margin: {
                            xs: 0,
                            sm: "0 3px",
                          },
                        }}
                        {...item}
                        slots={paginationSlots}
                        slotProps={{
                          previous: { onClick: handlePreviousClick },
                          next: {
                            onClick: handleNextClick,
                            disabled: page === Math.ceil(totalRecords / 12),
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              )}
            </Container>
          </Box>
        </Box>
      </Background>
    </>
  );
}
